'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { useUser } from '@/app_lib/providers/UserProvider';
import { pianoInit, pianoRunExperience } from '@/app_lib/scripts/piano/utils';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { PIANO_CDN_URL } from '@/app_lib/services/tinypass/service';
import {
  PianoConfig,
  type PianoPageConfig,
  type ResponseVariables,
  type SetterMap,
} from '@/app_lib/types/accounts/piano/Piano';
import type { EventData } from '@/app_lib/types/Gtm';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

export const PianoScript = () => {
  const [pianoIsInitalized, setPianoIsInitalized] = useState(false);
  const { pianoLoadingState, setPianoLoadingState, pianoPageConfig, setStopVideoDock, performanceTracking } = useUser();
  const { setPaywall30, setShowPaywallax } = usePaywall();
  const { setHideOutbrain, setHideInStreamAds } = useAdContext();
  const { optimizely } = useOptimizely();

  const setterMap: SetterMap = useMemo(
    () => ({
      hideInStreamAds: (val) => setHideInStreamAds(val),
      hideOutbrain: (val) => setHideOutbrain(val),
      paywall30: (val) => setPaywall30(val),
      showPaywallax: (val) => setShowPaywallax(val),
      stopVideoDock: (val) => setStopVideoDock(val),
    }),
    [setPaywall30, setShowPaywallax, setHideOutbrain, setStopVideoDock, setHideInStreamAds],
  );

  const sendEventOnConsent = useCallback(
    (event: EventData) => {
      try {
        if (performanceTracking) {
          trackEvent(event);
        }
      } catch (_error) {
        // console.error('Error processing paywall funnel event:', error);
      }
    },
    [performanceTracking],
  );

  const updateResponseVariables = useCallback(
    (responseVariables: Partial<ResponseVariables>) => {
      Object.entries(responseVariables).forEach(([key, value]) => {
        if (key in setterMap) {
          setterMap[key](value);
        }
      });
    },
    [setterMap],
  );

  const isBypassed = useMemo(() => {
    return pianoPageConfig?.contentSection === PianoConfig.BypassSection;
  }, [pianoPageConfig]);

  const pathname = usePathname();
  const lastPianoPageConfigRan = useRef<PianoPageConfig | undefined>(undefined);

  useEffect(() => {
    if (pianoLoadingState === 'loaded' && !pianoIsInitalized && pianoPageConfig) {
      setPianoIsInitalized(
        pianoInit(pianoPageConfig, isBypassed, updateResponseVariables, sendEventOnConsent, optimizely),
      );
      lastPianoPageConfigRan.current = pianoPageConfig;
    }
  }, [
    pianoLoadingState,
    pianoIsInitalized,
    pianoPageConfig,
    isBypassed,
    updateResponseVariables,
    sendEventOnConsent,
    optimizely,
  ]);

  // Reset paywall state on SPA navigation
  useEffect(() => {
    setShowPaywallax(false);
    setPaywall30('pending');
    setHideOutbrain(false);
    setHideInStreamAds(false);
    setStopVideoDock(false);
  }, [pathname]);

  // Run experience when pianoPageConfig updates (e.g. after SPA navigation when the new page's wrapper calls setPianoPageConfig).
  // Waits for the client-updated config for the new route before pushing to tp and executing.
  useEffect(() => {
    if (!pianoIsInitalized) {
      return;
    }
    if (!pianoPageConfig) {
      return;
    }
    const isNewConfig = lastPianoPageConfigRan.current !== pianoPageConfig;
    if (!isNewConfig) {
      return;
    }
    lastPianoPageConfigRan.current = pianoPageConfig;
    pianoRunExperience(pianoPageConfig, isBypassed);
  }, [pianoIsInitalized, pianoPageConfig, isBypassed]);

  return (
    <>
      <Script
        id='cxense-script'
        src='https://cdn.cxense.com/ari.js'
        strategy='afterInteractive'
      />
      <Script
        id='piano-id'
        onError={() => {
          setPianoLoadingState('failed');
          setPaywall30('no-paywall');
        }}
        onLoad={() => setPianoLoadingState('loaded')}
        src={PIANO_CDN_URL}
        strategy='afterInteractive'
      />
    </>
  );
};
